/**
 * A seção Planos da administração (docs/spec/planos.md, "Editar os planos"): os quatro planos lado a
 * lado, com o nome, os preços (o Pix em real, o cartão em dólar), o que cada um dá, quantas contas estão nele e quantas passam do espaço.
 * Os admins da configuração do servidor editam e voltam um plano ao padrão; os promovidos só veem.
 */
import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { useLanguage, useT } from '../i18n/index.js';
import { PRICE_INFO, PRICE_KEYS, entitlementsFor, type AdminPlanRow, type PlanCeilings, type PlanGrid } from '../shared.js';
import { ENTITLEMENT_KEYS, entitlementValue, planName, priceText } from '../plans/planModel.js';
import { describeError, useApp } from '../store.js';
import { Button, Spinner } from '../ui/controls.js';
import { useMobile } from '../ui/mobile.js';
import { askConfirm } from '../ui/prompt.js';
import { toast } from '../ui/Toaster.js';
import { AdminPlanEditDialog } from './AdminPlanEditDialog.js';

type PlansPage = { plans: AdminPlanRow[]; ceilings: PlanCeilings; canEdit: boolean };

export function AdminPlans({ version, onChanged }: { version: number; onChanged: () => void }) {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const token = useApp((s) => s.token);
  const mobile = useMobile();
  const [page, setPage] = useState<PlansPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminPlanRow | null>(null);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    api.adminPlans(token).then(
      (result) => {
        if (!alive) return;
        setPage(result);
        setError(null);
      },
      (failure: unknown) => {
        if (alive) setError(describeError(failure));
      },
    );
    return () => {
      alive = false;
    };
  }, [token, version]);

  /** O nome que vale de um plano, na língua da tela. */
  const nameOf = (row: AdminPlanRow) => planName(t, row.id, row.config.names, language);

  const reset = async (row: AdminPlanRow) => {
    if (!token) return;
    const sure = await askConfirm({ title: t('admin.plans.reset'), message: t('admin.plans.resetConfirm', { plan: nameOf(row) }), action: t('admin.plans.reset'), danger: true });
    if (!sure) return;
    try {
      await api.adminResetPlanConfig(token, row.id);
      toast(t('admin.plans.resetDone', { plan: planName(t, row.id, null, language) }), 'success');
      onChanged();
    } catch (failure) {
      toast(describeError(failure), 'error');
    }
  };

  if (error) {
    return (
      <p className="rd-adminUsers__error" role="alert">
        {error}
      </p>
    );
  }
  if (!page) {
    return (
      <div className="rd-placeholder">
        <Spinner size={28} />
      </div>
    );
  }

  // o que cada plano dá, já com o teto do servidor (o que vale de fato)
  const grid = Object.fromEntries(page.plans.map((r) => [r.id, r.config.entitlements])) as PlanGrid;
  const effective = (row: AdminPlanRow) => entitlementsFor(row.id, page.ceilings, grid);
  const facts = (row: AdminPlanRow): { label: string; value: string; warn?: boolean }[] => [
    ...PRICE_KEYS.map((key) => ({ label: t(`admin.plans.price.${key}`), value: priceText(t, row.config.prices[key], PRICE_INFO[key].currency, language) })),
    ...ENTITLEMENT_KEYS.map((key) => {
      const value = row.config.entitlements[key];
      const holds = effective(row)[key];
      return { label: t(`plans.entitlement.${key}`), value: holds === value ? entitlementValue(t, key, value) : t('admin.plans.cut', { value: entitlementValue(t, key, value), holds: entitlementValue(t, key, holds) }) };
    }),
    { label: t('admin.plans.accounts'), value: String(row.accounts) },
    { label: t('admin.plans.overSpace'), value: String(row.overSpace), warn: row.overSpace > 0 },
  ];
  const actions = (row: AdminPlanRow) =>
    page.canEdit ? (
      <div className="rd-adminPlans__actions">
        <Button onClick={() => setEditing(row)}>{t('admin.plans.edit')}</Button>
        {row.edited ? (
          <Button className="rd-btn--danger" onClick={() => void reset(row)}>
            {t('admin.plans.reset')}
          </Button>
        ) : null}
      </div>
    ) : null;
  const head = (row: AdminPlanRow) => (
    <>
      <span className="rd-adminPlans__name">{nameOf(row)}</span>
      {row.edited ? (
        <span className="rd-adminBadge rd-adminBadge--edited" title={row.updatedByName ? t('admin.plans.editedBy', { name: row.updatedByName }) : undefined}>
          {t('admin.plans.edited')}
        </span>
      ) : null}
    </>
  );

  return (
    <section className="rd-adminPlans" aria-label={t('admin.section.plans')}>
      <p className="rd-adminPlans__hint">{page.canEdit ? t('admin.plans.hint') : t('admin.plans.readOnly')}</p>
      {mobile ? (
        <ul className="rd-adminPlans__cards">
          {page.plans.map((row) => (
            <li key={row.id} aria-label={nameOf(row)}>
              <h3>{head(row)}</h3>
              <dl>
                {facts(row).map((fact) => (
                  <div key={fact.label} className={fact.warn ? 'is-warn' : undefined}>
                    <dt>{fact.label}</dt>
                    <dd>{fact.value}</dd>
                  </div>
                ))}
              </dl>
              {actions(row)}
            </li>
          ))}
        </ul>
      ) : (
        <table className="rd-adminPlans__table" aria-label={t('admin.section.plans')}>
          <thead>
            <tr>
              <th scope="col" />
              {page.plans.map((row) => (
                <th key={row.id} scope="col">
                  {head(row)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {facts(page.plans[0]!).map((_, i) => (
              <tr key={i}>
                <th scope="row">{facts(page.plans[0]!)[i]!.label}</th>
                {page.plans.map((row) => {
                  const fact = facts(row)[i]!;
                  return (
                    <td key={row.id} className={fact.warn ? 'is-warn' : undefined}>
                      {fact.value}
                    </td>
                  );
                })}
              </tr>
            ))}
            {page.canEdit ? (
              <tr>
                <th scope="row" />
                {page.plans.map((row) => (
                  <td key={row.id}>{actions(row)}</td>
                ))}
              </tr>
            ) : null}
          </tbody>
        </table>
      )}
      {editing ? (
        <AdminPlanEditDialog
          row={editing}
          plans={page.plans}
          ceilings={page.ceilings}
          onDone={(saved) => {
            setEditing(null);
            if (saved) onChanged();
          }}
        />
      ) : null}
    </section>
  );
}
